import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Chart } from "chart.js/auto";
import { useAppContext } from "components/Context/AppContext";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import { fmtDec } from "utils/format";
import "styles/dashboard.css";
import "./historical.css";

const DASHBOARD_API = process.env.REACT_APP_API_URL || "http://localhost:5050/api";

// ── Theme helpers ──────────────────────────────────────────────────────────────
const FONT_M = "'DM Mono', monospace";
const FONT_B = "'Outfit', sans-serif";
const LINE_COLORS = ['#4e8fdd', '#22d47e', '#C9A84C', '#b87bc8', '#e05252'];
const BAR_COLORS  = [
  'rgba(78,143,221,0.85)', 'rgba(34,212,126,0.85)', 'rgba(201,168,76,0.85)',
  'rgba(184,123,200,0.85)', 'rgba(224,82,82,0.85)',
];
const gridCol    = (lt) => lt ? 'rgba(0,0,0,0.06)'    : 'rgba(255,255,255,0.05)';
const textCol    = (lt, a) => lt ? `rgba(0,0,0,${a})` : `rgba(255,255,255,${a})`;
const tooltipOpts = (lt) => ({
  backgroundColor: lt ? 'rgba(255,255,255,0.97)' : 'rgba(8,14,24,0.96)',
  borderColor:     lt ? 'rgba(0,0,0,0.12)'       : 'rgba(255,255,255,0.08)',
  borderWidth: 1, padding: 8,
  titleColor: textCol(lt, 0.55), bodyColor: textCol(lt, 0.85),
  titleFont: { family: FONT_M, size: 10 },
  bodyFont:  { family: FONT_M, size: 12 },
});

// ── Chart plugin factories ─────────────────────────────────────────────────────
function makeLimitPlugin(uid, limit, suffix, prefix = '') {
  return { id: 'lim_' + uid, afterDraw(ch) {
    const { ctx: c, scales: { y }, chartArea: { left, right } } = ch; if (!y) return;
    const py = y.getPixelForValue(limit), tk = 10;
    c.save();
    c.strokeStyle = '#22d47e'; c.lineWidth = 1; c.globalAlpha = 0.35;
    c.beginPath(); c.moveTo(left, py); c.lineTo(right, py); c.stroke();
    c.globalAlpha = 1; c.lineWidth = 2.5;
    c.beginPath(); c.moveTo(right - 2, py - tk); c.lineTo(right - 2, py + tk); c.stroke();
    c.fillStyle = '#22d47e'; c.font = `500 9px ${FONT_M}`;
    c.textAlign = 'right'; c.textBaseline = 'bottom'; c.globalAlpha = 0.9;
    c.fillText(`Limit ${prefix}${limit}${suffix || '%'}`, right - 6, py - 2);
    c.restore();
  }};
}
function makeTargetPlugin(uid, target) {
  return { id: 'tgt_' + uid, afterDraw(ch) {
    const { ctx: c, scales: { y }, chartArea: { left, right } } = ch; if (!y) return;
    const py = y.getPixelForValue(target), tk = 10;
    c.save();
    c.strokeStyle = '#22d47e'; c.lineWidth = 1; c.globalAlpha = 0.35;
    c.beginPath(); c.moveTo(left, py); c.lineTo(right, py); c.stroke();
    c.globalAlpha = 1; c.lineWidth = 2.5;
    c.beginPath(); c.moveTo(right - 2, py - tk); c.lineTo(right - 2, py + tk); c.stroke();
    c.fillStyle = '#22d47e'; c.font = `500 9px ${FONT_M}`;
    c.textAlign = 'right'; c.textBaseline = 'bottom'; c.globalAlpha = 0.9;
    c.fillText(`Target ${target}`, right - 6, py - 2);
    c.restore();
  }};
}
function makeBarLabelPlugin(uid) {
  return { id: 'blbl_' + uid, afterDatasetsDraw(chart) {
    chart.data.datasets.forEach((ds, di) => {
      chart.getDatasetMeta(di).data.forEach((bar, idx) => {
        const val = ds.data[idx]; if (val == null || val < 3) return;
        const { x, y, height } = bar.getProps(['x', 'y', 'height'], true);
        if (Math.abs(height) < 14) return;
        chart.ctx.save();
        chart.ctx.fillStyle = 'rgba(255,255,255,0.88)';
        chart.ctx.font = `500 9px ${FONT_M}`;
        chart.ctx.textAlign = 'center'; chart.ctx.textBaseline = 'middle';
        chart.ctx.fillText(val.toFixed(1) + '%', x, y + height / 2);
        chart.ctx.restore();
      });
    });
  }};
}
function makeStackedLimitPlugin(uid, limit, limitLabel) {
  return { id: 'slim_' + uid, afterDraw(chart) {
    const { ctx: c, scales: { y }, chartArea: { left, right } } = chart; if (!y) return;
    const py = y.getPixelForValue(limit);
    c.save(); c.strokeStyle = '#22d47e'; c.lineWidth = 1.5; c.setLineDash([5, 3]);
    c.beginPath(); c.moveTo(left, py); c.lineTo(right, py); c.stroke();
    c.setLineDash([]); c.fillStyle = '#22d47e'; c.font = `500 9px ${FONT_M}`;
    c.textAlign = 'right'; c.textBaseline = 'bottom';
    c.fillText(limitLabel || `Limit ${limit}%`, right - 4, py - 3);
    c.restore();
  }};
}

// ── Concentration mini chart (single category, limit line + playhead) ─────────
function ConcMiniChart({ categoryName, seriesData, limit, labels, activeT, isLight, chartDeps }) {
  const canvasRef   = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (instanceRef.current) { instanceRef.current.destroy(); instanceRef.current = null; }

    const lineColor  = '#4e8fdd';
    const limitColor = '#e05252';
    const T          = labels.length;
    const nonNull    = seriesData.filter(v => v != null);
    const seriesMax  = nonNull.length ? Math.max(...nonNull) : 1;
    const yMax       = Math.max(+(seriesMax * 1.18).toFixed(2), limit ?? 0, 0.1);

    const plugin = {
      id: 'cminiLPH_' + categoryName.replace(/\s+/g, '_'),
      afterDatasetsDraw(ch) {
        const { ctx: c, chartArea: ca, scales: { x, y } } = ch;
        if (!ca || !y) return;
        if (limit != null) {
          const ly = y.getPixelForValue(limit);
          if (ly >= ca.top && ly <= ca.bottom) {
            c.save();
            c.strokeStyle = limitColor; c.lineWidth = 1.25; c.setLineDash([4, 3]);
            c.beginPath(); c.moveTo(ca.left, ly); c.lineTo(ca.right, ly); c.stroke();
            c.setLineDash([]); c.restore();
          }
        }
        const t = ch.options.activeT;
        if (t == null || t < 0 || t >= T) return;
        const px  = x.getPixelForValue(t);
        const val = seriesData[t];
        if (val == null || px < ca.left || px > ca.right) return;
        const py = y.getPixelForValue(val);
        c.save();
        c.strokeStyle = 'rgba(201,168,76,0.55)'; c.lineWidth = 1; c.setLineDash([2, 2]);
        c.beginPath(); c.moveTo(px, ca.top); c.lineTo(px, ca.bottom); c.stroke();
        c.setLineDash([]);
        c.fillStyle   = '#C9A84C';
        c.strokeStyle = isLight ? '#ffffff' : '#0b1422';
        c.lineWidth   = 1.5;
        c.beginPath(); c.arc(px, py, 3.2, 0, Math.PI * 2); c.fill(); c.stroke();
        c.restore();
      },
    };

    instanceRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: categoryName, data: seriesData,
          borderColor: lineColor, backgroundColor: 'rgba(78,143,221,0.10)',
          borderWidth: 1.6, pointRadius: 0, pointHoverRadius: 4,
          pointBackgroundColor: lineColor, tension: 0.3, fill: true,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        layout: { padding: { top: 4, right: 2, bottom: 0, left: 2 } },
        interaction: { mode: 'index', intersect: false },
        activeT: Math.max(0, T - 1),
        plugins: {
          legend: { display: false },
          tooltip: {
            ...tooltipOpts(isLight),
            callbacks: {
              title: items => labels[items[0].dataIndex],
              label: c => ` ${categoryName}: ${fmtDec(c.parsed.y)}%${limit != null ? `  (limit ${fmtDec(limit)}%)` : ''}`,
            },
          },
        },
        scales: {
          x: { display: false, grid: { display: false } },
          y: {
            min: 0, max: yMax,
            grid: { color: gridCol(isLight), drawTicks: false },
            border: { display: false },
            ticks: { color: textCol(isLight, 0.4), font: { family: FONT_M, size: 8 }, maxTicksLimit: 3, padding: 3, callback: v => v.toFixed(1) + '%' },
          },
        },
      },
      plugins: [plugin],
    });

    return () => { if (instanceRef.current) { instanceRef.current.destroy(); instanceRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, chartDeps);

  useEffect(() => {
    const ch = instanceRef.current;
    if (!ch) return;
    ch.options.activeT = activeT;
    ch.update('none');
  }, [activeT]);

  return <div className="hist-cmini-canvaswrap"><canvas ref={canvasRef} /></div>;
}

// ── Concentrations vs Limits mini-grid with shared playhead slider ────────────
function ConcMiniGrid({ concChart, labels, isLight }) {
  const T       = labels?.length ?? 0;
  const [activeT, setActiveT] = useState(Math.max(0, T - 1));

  useEffect(() => { setActiveT(Math.max(0, T - 1)); }, [T]);

  if (!concChart?.series?.length) return null;

  const chartDeps = [concChart, isLight];

  return (
    <div className="hist-conc-card">
      <div className="hist-conc-title">Concentrations vs Limits</div>
      <div className="hist-cmini-grid">
        {concChart.series.map(s => (
          <div key={s.name} className="hist-cmini">
            <div className="hist-cmini-name">{s.name}</div>
            <ConcMiniChart
              categoryName={s.name}
              seriesData={s.data}
              limit={s.limit ?? null}
              labels={labels}
              activeT={activeT}
              isLight={isLight}
              chartDeps={chartDeps}
            />
          </div>
        ))}
      </div>
      {T > 1 && (
        <div className="hist-cmini-controls">
          <span className="hist-cmini-ctl-label">Snapshot</span>
          <input
            type="range" className="hist-cmini-slider"
            min={0} max={T - 1} step={1} value={activeT}
            onChange={e => setActiveT(+e.target.value)}
          />
          <span className="hist-cmini-tlabel">{labels[activeT] ?? '—'}</span>
        </div>
      )}
    </div>
  );
}

// ── Reusable chart canvas ──────────────────────────────────────────────────────
function CjsChart({ buildConfig, deps, wrapClass }) {
  const canvasRef   = useRef(null);
  const instanceRef = useRef(null);
  useEffect(() => {
    if (!canvasRef.current) return;
    if (instanceRef.current) { instanceRef.current.destroy(); instanceRef.current = null; }
    instanceRef.current = new Chart(canvasRef.current, buildConfig());
    return () => { if (instanceRef.current) { instanceRef.current.destroy(); instanceRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return <div className={wrapClass}><canvas ref={canvasRef} /></div>;
}

// ── Alloc chart definitions (title + API key) ──────────────────────────────────
const ALLOC_CHART_DEFS = [
  { title: 'Asset Class Allocation',         key: 'assetAlloc'    },
  { title: 'Asset Class — VaR Contribution', key: 'assetVar'      },
  { title: 'Region Allocation',              key: 'regionAlloc'   },
  { title: 'Region — VaR Contribution',      key: 'regionVar'     },
  { title: 'Industry Allocation',            key: 'industryAlloc' },
  { title: 'Industry — VaR Contribution',    key: 'industryVar'   },
  { title: 'Currency Allocation',            key: 'ccyAlloc'      },
  { title: 'Currency — VaR Contribution',    key: 'ccyVar'        },
];

// ── Main component ─────────────────────────────────────────────────────────────
export default function HistoricalPage() {
  const { selectedAccount }               = useAppContext();
  const [theme, setTheme]                 = useState(() => localStorage.getItem("trg_theme") || "dark");
  const [dailyData, setDailyData]         = useState(null);
  const [weeklyData, setWeeklyData]       = useState(null);
  const [expandedTitle, setExpandedTitle] = useState(null);

  const isLight = theme === "light";

  // ── Fetch ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedAccount) return;
    const token = sessionStorage.getItem("token");
    const base  = `account_id=${selectedAccount.account_id}&token=${token}`;
    fetch(`${DASHBOARD_API}/historical?${base}&freq=daily`)
      .then(r => r.json())
      .then(d => { if (d && d.charts) setDailyData(d); })
      .catch(console.error);
    fetch(`${DASHBOARD_API}/historical?${base}&freq=weekly`)
      .then(r => r.json())
      .then(d => { if (d && d.charts) setWeeklyData(d); })
      .catch(console.error);
  }, [selectedAccount]);

  const dailyChartDeps  = [dailyData, theme];
  const weeklyChartDeps = [weeklyData, theme];

  // ── Line chart builder ────────────────────────────────────────────────────────
  // Handles single-series (vol, var, sharpeVol, sharpeVar) and multi-series (concentrations).
  // suffix/decimals are display concerns: the backend does not dictate formatting.
  function buildLineConfig(chartBlock, labels, { prefix = '', suffix = '%', decimals = 1, fromZero = false } = {}) {
    const empty = { type: 'line', data: { labels: [], datasets: [] }, options: { plugins: { legend: { display: false } } } };
    if (!chartBlock || !chartBlock.series?.length) return empty;
    const { series, limit, target } = chartBlock;
    const isMulti = series.length > 1;

    const datasets = series.map((s, i) => ({
      label:           s.name,
      data:            s.data,
      borderColor:     isMulti ? LINE_COLORS[i % LINE_COLORS.length] : '#4e8fdd',
      backgroundColor: isMulti ? 'transparent' : 'rgba(78,143,221,0.08)',
      borderWidth: 2, pointRadius: 0, pointHoverRadius: 5, tension: 0.3,
      fill: !isMulti,
    }));

    const allVals = series.flatMap(s => s.data).filter(v => v != null && !isNaN(v));
    const refs    = [limit, target].filter(v => v != null);
    const allV    = [...allVals, ...refs];
    if (!allV.length) allV.push(0, 1);
    const rMin = Math.min(...allV), rMax = Math.max(...allV), rPad = (rMax - rMin) * 0.3 || 0.5;
    const yMin = fromZero ? 0 : Math.max(0, rMin - rPad);
    const yMax = fromZero ? rMax * 1.25 : rMax + rPad;

    const uid     = series[0].name;
    const plugins = [];
    if (limit  != null) plugins.push(makeLimitPlugin(uid, limit, suffix, prefix));
    if (target != null) plugins.push(makeTargetPlugin(uid, target));

    return {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: isMulti, position: 'bottom', labels: { color: textCol(isLight, 0.55), font: { family: FONT_B, size: 10 }, boxWidth: 10, padding: 10 } },
          tooltip: { ...tooltipOpts(isLight), callbacks: {
            title: items => labels[items[0].dataIndex],
            label: c => ' ' + c.dataset.label + ': ' + prefix + c.parsed.y.toFixed(decimals) + suffix,
          }},
        },
        scales: {
          x: { grid: { color: gridCol(isLight) }, border: { color: gridCol(isLight) }, ticks: { color: textCol(isLight, 0.5), font: { family: FONT_M, size: 9 }, maxRotation: 0, maxTicksLimit: 9 } },
          y: { grid: { color: gridCol(isLight) }, border: { color: gridCol(isLight) }, min: yMin, max: yMax,
               ticks: { color: textCol(isLight, 0.3), font: { family: FONT_M, size: 10 }, callback: v => prefix + v.toFixed(decimals) + suffix } },
        },
      },
      plugins,
    };
  }

  // ── P&L vs VaR builder ────────────────────────────────────────────────────────
  // Special case: series 0 (PnL) uses sign-colored points; series 1 (VaR) is dashed.
  function buildPnlConfig(chartBlock, labels) {
    const empty = { type: 'line', data: { labels: [], datasets: [] }, options: { plugins: { legend: { display: false } } } };
    if (!chartBlock?.series?.length) return empty;
    const [pnl, varSer] = chartBlock.series;
    const unit          = chartBlock.unit;                          // 'M', 'K', or undefined (legacy 'mm')
    const suffix        = unit ? unit : 'mm';
    const fmtV = (v, addSign = false) =>
      (addSign && v >= 0 ? '+' : '') + (unit ? '$' : '') + v.toFixed(1) + suffix;
    return {
      type: 'line',
      data: { labels, datasets: [
        { label: pnl.name,    data: pnl.data,    borderColor: '#22d47e', backgroundColor: 'rgba(34,212,126,0.08)', borderWidth: 2, pointRadius: 0, pointHoverRadius: 5, pointBackgroundColor: pnl.data.map(v => v == null || v >= 0 ? '#22d47e' : '#e05252'), pointBorderColor: isLight ? '#fff' : '#0f1a2a', pointBorderWidth: 1.5, tension: 0.3, fill: true },
        { label: varSer.name, data: varSer.data, borderColor: '#e05252', borderDash: [5, 3], borderWidth: 2, pointRadius: 0, pointHoverRadius: 5, pointBackgroundColor: '#e05252', pointBorderColor: isLight ? '#fff' : '#0f1a2a', pointBorderWidth: 1.5, tension: 0.3, fill: false },
      ]},
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: { ...tooltipOpts(isLight), callbacks: { label: ctx => ' ' + ctx.dataset.label + ': ' + fmtV(ctx.parsed.y, ctx.datasetIndex === 0) } },
        },
        scales: {
          x: { grid: { color: gridCol(isLight) }, border: { color: 'transparent' }, ticks: { color: textCol(isLight, 0.5), font: { family: FONT_M, size: 10 }, maxRotation: 0, maxTicksLimit: 9 } },
          y: { grid: { color: gridCol(isLight) }, border: { color: 'transparent' }, ticks: { color: textCol(isLight, 0.35), font: { family: FONT_M, size: 10 }, callback: v => (unit ? '$' : '') + v.toFixed(1) + suffix } },
        },
      },
    };
  }

  // ── Stacked bar builder ────────────────────────────────────────────────────────
  function buildStackedBarConfig(chartBlock, labels) {
    const empty = { type: 'bar', data: { labels: [], datasets: [] }, options: { plugins: { legend: { display: false } } } };
    if (!chartBlock || !chartBlock.series?.length) return empty;
    const { series, limit, limitLabel } = chartBlock;
    const plugins = [makeBarLabelPlugin(series[0].name)];
    if (limit != null) plugins.push(makeStackedLimitPlugin(series[0].name, limit, limitLabel));
    return {
      type: 'bar',
      data: {
        labels,
        datasets: series.map((s, i) => ({ label: s.name, data: s.data, backgroundColor: BAR_COLORS[i % BAR_COLORS.length], borderColor: 'transparent', borderWidth: 0 })),
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'bottom', labels: { color: textCol(isLight, 0.55), font: { family: FONT_B, size: 10 }, boxWidth: 10, padding: 10 } },
          tooltip: { ...tooltipOpts(isLight), callbacks: { title: items => labels[items[0].dataIndex], label: c => ' ' + c.dataset.label + ': ' + fmtDec(c.parsed.y) + '%' } },
        },
        scales: {
          x: { stacked: true, grid: { display: false }, border: { color: gridCol(isLight) }, ticks: { color: textCol(isLight, 0.5), font: { family: FONT_M, size: 9 } } },
          y: { stacked: true, min: 0, max: 100, grid: { color: gridCol(isLight) }, border: { color: gridCol(isLight) }, ticks: { color: textCol(isLight, 0.3), font: { family: FONT_M, size: 10 }, callback: v => v + '%' } },
        },
      },
      plugins,
    };
  }

  const expandedDef = expandedTitle ? ALLOC_CHART_DEFS.find(c => c.title === expandedTitle) : null;

  return (
    <div className={`trg-shell${isLight ? " theme-light" : ""}`}>
      <Helmet><title>Historical — Tail Risk Global</title></Helmet>
      <DashboardTopbar />
      <DashboardMenuBar onThemeChange={setTheme} />

      <div className="hist-body">

        <div className="hist-page-hdr">
          <div>
            <div className="hist-page-title">Historical Analysis</div>
            <div className="hist-page-sub">Compare one portfolio across multiple time snapshots</div>
          </div>
        </div>

        {dailyData && <>
          <div className="hist-metrics-grid">
            <div className="hist-metric-card">
              <div className="hist-metric-title">Volatility</div>
              <CjsChart buildConfig={() => buildLineConfig(dailyData.charts.volatility, dailyData.labels, { suffix: '%', decimals: 1 })} deps={dailyChartDeps} wrapClass="hist-metric-canvas-wrap" />
            </div>
            <div className="hist-metric-card">
              <div className="hist-metric-title">VaR (95% 1D)</div>
              <CjsChart buildConfig={() => { const u = dailyData.charts.var?.unit; const isDollar = u === 'M' || u === 'K'; return buildLineConfig(dailyData.charts.var, dailyData.labels, { prefix: isDollar ? '$' : '', suffix: isDollar ? u : '%', decimals: u === 'K' ? 0 : 1 }); }} deps={dailyChartDeps} wrapClass="hist-metric-canvas-wrap" />
            </div>
            <div className="hist-metric-card">
              <div className="hist-metric-title" title="Risk-Adjusted Expected Return vs Benchmark — scaled by volatility">Sharpe Ratio (Vol) vs BMK</div>
              <CjsChart buildConfig={() => buildLineConfig(dailyData.charts.sharpeVol, dailyData.labels, { suffix: '', decimals: 2 })} deps={dailyChartDeps} wrapClass="hist-metric-canvas-wrap" />
            </div>
            <div className="hist-metric-card">
              <div className="hist-metric-title" title="Risk-Adjusted Expected Return vs Benchmark — scaled by VaR">Sharpe Ratio (VaR) vs BMK</div>
              <CjsChart buildConfig={() => buildLineConfig(dailyData.charts.sharpeVar, dailyData.labels, { suffix: '', decimals: 2 })} deps={dailyChartDeps} wrapClass="hist-metric-canvas-wrap" />
            </div>
          </div>

          <div className="hist-wide-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
              <div className="hist-wide-title" style={{ marginBottom: 0 }}>P&amp;L vs VaR</div>
              <div style={{ display: 'flex', gap: '14px', fontFamily: 'var(--dash-font-b)', fontSize: '0.48rem', color: 'var(--dash-muted)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: 18, height: 2, background: '#22d47e', display: 'inline-block', borderRadius: 2 }} />P&amp;L ($mm)</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: 18, height: 2, background: '#e05252', display: 'inline-block', borderRadius: 2 }} />VaR 95% ($mm)</span>
              </div>
            </div>
            <CjsChart buildConfig={() => buildPnlConfig(dailyData.charts.pnlVsVar, dailyData.labels)} deps={dailyChartDeps} wrapClass="hist-wide-canvas-wrap" />
          </div>

          <ConcMiniGrid concChart={dailyData.charts.concentrations} labels={dailyData.labels} isLight={isLight} />
        </>}

        {weeklyData && (
          <div className="hist-alloc-grid">
            {ALLOC_CHART_DEFS.map(({ title, key }) => (
              <div key={title} className="hist-alloc-card">
                <div className="hist-alloc-card-hdr">
                  <div className="hist-alloc-title">{title}</div>
                  <button className="hist-alloc-expand-btn" title="Expand" onClick={() => setExpandedTitle(title)}>⤢</button>
                </div>
                <CjsChart buildConfig={() => buildStackedBarConfig(weeklyData.charts[key], weeklyData.labels)} deps={weeklyChartDeps} wrapClass="hist-alloc-canvas-wrap" />
              </div>
            ))}
          </div>
        )}

      </div>

      {expandedDef && weeklyData && (
        <div className="hist-fs-overlay">
          <div className="hist-fs-header">
            <div className="hist-fs-title">{expandedTitle}</div>
            <button className="hist-fs-close" onClick={() => setExpandedTitle(null)}>✕ close</button>
          </div>
          <div className="hist-fs-canvas-wrap">
            <CjsChart buildConfig={() => buildStackedBarConfig(weeklyData.charts[expandedDef.key], weeklyData.labels)} deps={[...weeklyChartDeps, expandedTitle]} wrapClass="hist-fs-canvas-inner" />
          </div>
        </div>
      )}

    </div>
  );
}
